/**
 * Unified LinkedTable Editor Hook
 *
 * Simplified hook for managing linkedTable configuration and prelinkedTable updates.
 *
 * This hook provides:
 * - PrelinkedTable configuration (only complete elements)
 * - PrelinkedTable key for cache busting
 * - Incomplete counts for UI badges
 * - Event handlers for all user actions
 */

import { useMemo, useRef, useCallback } from 'react';
import type {
  TableFieldApiResponse,
  LinkedTableNormalizedConfig,
  LinkedTableConfigChanges,
} from '@loader/shared-types';
import { parseFieldId } from '@loader/shared-types';

import { isFieldMappingComplete } from '@/features/linkedTables/editLinkedTable/utils/joinConditionMapper';
import { toast } from '@/lib/toast';
import type {
  LinkedTableFilter,
  LinkedTableCalculatedColumn,
  LinkedTableRelationship,
  LinkedTableValue,
  LinkedTableRowGroupSort,
  LinkedTableVariable,
} from '@/lib/types';

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Configuration for all linkedTable changes
 */
export interface LinkedTableEditorConfig {
  filters: LinkedTableFilter[];
  calculated_columns: LinkedTableCalculatedColumn[];
  relationships: LinkedTableRelationship[];
  versionSelections: Record<string, string>;
  row_groups: string[];
  row_group_sorts: LinkedTableRowGroupSort[];
  values: LinkedTableValue[];
  variables: LinkedTableVariable[];
}

/**
 * PrelinkedTable configuration (only complete items, used for query cache key)
 */
export interface PrelinkedTableConfig {
  filters: LinkedTableFilter[];
  relationships: LinkedTableRelationship[];
  calculated_columns: LinkedTableCalculatedColumn[];
  row_groups: string[];
  row_group_sorts: LinkedTableRowGroupSort[];
  values: LinkedTableValue[];
  variablesQueryKey: string;
  versionSelections: Record<string, string>;
}

/**
 * Incomplete counts for UI badges
 */
export interface IncompleteCounts {
  filters: number;
  relationships: number;
  calculated_columns: number;
}

/**
 * Props for useLinkedTableEditor hook
 */
interface UseLinkedTableEditorProps {
  // Current config
  config: LinkedTableEditorConfig;
  // State setters
  setFilters: React.Dispatch<React.SetStateAction<LinkedTableFilter[]>>;
  setCalculatedColumns: React.Dispatch<
    React.SetStateAction<LinkedTableCalculatedColumn[]>
  >;
  setRelationships: React.Dispatch<React.SetStateAction<LinkedTableRelationship[]>>;
  setVersionSelections: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setRowGroups: React.Dispatch<React.SetStateAction<string[]>>;
  setRowGroupSorts: React.Dispatch<React.SetStateAction<LinkedTableRowGroupSort[]>>;
  setValues: React.Dispatch<React.SetStateAction<LinkedTableValue[]>>;
  setVariables: React.Dispatch<React.SetStateAction<LinkedTableVariable[]>>;
  // Additional data
  tableFields: TableFieldApiResponse[];
  // SQL-native tables
  tables: Array<{ alias: string }>;
}

// ============================================================================
// Completeness Checkers
// ============================================================================

/**
 * Check if a filter is complete (has fieldId, operator, and value)
 */
export const isFilterComplete = (filter: LinkedTableFilter): boolean => {
  if (!filter.field_id || filter.field_id.trim() === '') return false;
  if (!filter.operator) return false;

  // For null/not null operators, value is not required
  if (filter.operator === 'is_null' || filter.operator === 'is_not_null') {
    return true;
  }

  // If a variable is bound, value is resolved at query time — no static value needed
  if (filter.variable_name) {
    return true;
  }

  // For other operators, value is required
  if (
    filter.value === '' ||
    filter.value === null ||
    filter.value === undefined
  ) {
    return false;
  }

  // If value is an array, it must have at least one item
  if (Array.isArray(filter.value)) {
    return filter.value.length > 0;
  }

  return true;
};

/**
 * Check if a calculated column is complete
 * A calculated column is complete if it has a name and formula
 * data_type is optional and defaults to 'number' if not set
 */
export const isCalculatedColumnComplete = (
  column: LinkedTableCalculatedColumn
): boolean => {
  return !!(column.name && column.formula_id);
};

/**
 * Check if a relationship is valid (basic fields present)
 */
export const isRelationshipValid = (
  relationship: LinkedTableRelationship
): boolean => {
  if (
    !relationship ||
    !relationship.target_id ||
    relationship.target_id.trim() === ''
  )
    return false;
  if (
    !relationship.target_type ||
    (relationship.target_type !== 'table' &&
      relationship.target_type !== 'linked_table')
  ) {
    return false;
  }
  if (!relationship.alias || relationship.alias.trim() === '') return false;
  return true;
};

/**
 * Check if a relationship is complete (has field mappings)
 */
export const isRelationshipComplete = (
  relationship: LinkedTableRelationship
): boolean => {
  if (!isRelationshipValid(relationship)) return false;
  const fieldMappings = relationship.field_mappings || [];
  if (fieldMappings.length === 0) return false;
  return fieldMappings.every(isFieldMappingComplete);
};

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Unified hook for linkedTable editor state management
 */
export const useLinkedTableEditor = ({
  config,
  setFilters,
  setCalculatedColumns,
  setRelationships,
  setVersionSelections,
  setRowGroups,
  setRowGroupSorts,
  setValues,
  setVariables,
  tableFields,
  tables,
}: UseLinkedTableEditorProps) => {
  // Refs to current values for stable access without triggering re-renders
  const configRef = useRef<LinkedTableEditorConfig>(config);
  const tableFieldsRef = useRef<TableFieldApiResponse[]>(tableFields);

  // Update refs on each render (no deps = runs after every render)
  configRef.current = config;
  tableFieldsRef.current = tableFields;

  // ============================================================================
  // PrelinkedTable Configuration
  // ============================================================================

  /**
   * Filter complete items first with STABLE references
   * CRITICAL: These memos prevent prelinkedTable updates when incomplete items change
   * Only when an item becomes complete (or a complete item changes) will these update
   *
   * Uses ID-based comparison to prevent unnecessary re-renders when the filtered
   * result is semantically the same (same IDs in same order)
   */
  const completeFilters = useMemo(() => {
    const filtered = config.filters.filter(isFilterComplete);
    const newIds = filtered.map(f => f.id).join(',');

    // Check if IDs changed compared to previous render (via ref)
    const prevFilters = configRef.current.filters.filter(isFilterComplete);
    const prevIds = prevFilters.map(f => f.id).join(',');

    // If IDs are the same, return previous reference to prevent downstream updates
    if (newIds === prevIds && JSON.stringify(prevFilters) === JSON.stringify(filtered)) {
      return prevFilters;
    }

    return filtered;
  }, [config.filters]);

  const completeRelationships = useMemo(() => {
    const filtered = config.relationships.filter(isRelationshipComplete);
    const newIds = filtered.map(r => r.id).join(',');

    const prevRelationships = configRef.current.relationships.filter(isRelationshipComplete);
    const prevIds = prevRelationships.map(r => r.id).join(',');

    if (newIds === prevIds && JSON.stringify(prevRelationships) === JSON.stringify(filtered)) {
      return prevRelationships;
    }

    return filtered;
  }, [config.relationships]);

  const completeCalculatedColumns = useMemo(() => {
    const filtered = config.calculated_columns.filter(isCalculatedColumnComplete);
    const newIds = filtered.map(c => c.id).join(',');

    const prevColumns = configRef.current.calculated_columns.filter(isCalculatedColumnComplete);
    const prevIds = prevColumns.map(c => c.id).join(',');

    if (newIds === prevIds && JSON.stringify(prevColumns) === JSON.stringify(filtered)) {
      return prevColumns;
    }

    return filtered;
  }, [config.calculated_columns]);

  /**
   * For preview cache-busting, only default_value and type matter (they affect query results).
   * Name is a cosmetic identifier — changing it doesn't affect the SQL until config is saved.
   * We serialize only query-affecting fields into a stable string for the preview key.
   */
  const variablesQueryKey = useMemo(() => {
    return config.variables
      .map(v => `${v.id}:${v.type}:${v.default_value}`)
      .join('|');
  }, [config.variables]);

  /**
   * PrelinkedTable configuration with only complete elements
   * This is the single source of truth for prelinkedTable queries
   * When this changes, the prelinkedTable will automatically update
   *
   * IMPORTANT: Depends on filtered complete arrays, not on raw config arrays
   * This prevents refetches when:
   * - User adds incomplete filter/relationship/calculated column
   * - User edits incomplete filter/relationship/calculated column
   * Only triggers refetch when:
   * - Item becomes complete
   * - Complete item is modified
   * - Complete item is removed
   */
  const prelinkedTableConfig = useMemo<PrelinkedTableConfig>(
    () => ({
      filters: completeFilters,
      relationships: completeRelationships,
      calculated_columns: completeCalculatedColumns,
      row_groups: config.row_groups, // Always complete
      row_group_sorts: config.row_group_sorts, // Always complete
      values: config.values, // Always complete
      variablesQueryKey, // Stable string — only changes when type/default_value change
      versionSelections: config.versionSelections, // Always complete
    }),
    [
      completeFilters,
      completeRelationships,
      completeCalculatedColumns,
      variablesQueryKey,
      config.row_groups,
      config.row_group_sorts,
      config.values,
      config.versionSelections,
    ]
  );

  /**
   * Serialized prelinkedTable key for TanStack Query cache busting
   * When this changes, prelinkedTable queries will automatically refetch
   */
  const prelinkedTableKey = useMemo(() => JSON.stringify(prelinkedTableConfig), [prelinkedTableConfig]);

  /**
   * Incomplete counts for UI badges (save button, section headers, etc.)
   */
  const incompleteCounts = useMemo<IncompleteCounts>(
    () => ({
      filters: config.filters.filter((f) => !isFilterComplete(f)).length,
      relationships: config.relationships.filter((r) => !isRelationshipComplete(r)).length,
      calculated_columns: config.calculated_columns.filter((c) => !isCalculatedColumnComplete(c)).length,
    }),
    [config.filters, config.relationships, config.calculated_columns]
  );

  // ============================================================================
  // Event Handlers - Filters
  // ============================================================================

  const handleAddFilter = useCallback(() => {
    const newFilter: LinkedTableFilter = {
      id: `filter-${Date.now()}`,
      table: tables.length > 0 ? tables[0].alias : '',
      field_id: '',
      operator: 'equals',
      value: '',
    };
    setFilters((prev) => [...prev, newFilter]);
  }, [setFilters, tables]);

  const handleUpdateFilter = useCallback(
    (updatedFilter: LinkedTableFilter) => {
      setFilters((prev) =>
        prev.map((f) => (f.id === updatedFilter.id ? updatedFilter : f))
      );
    },
    [setFilters]
  );

  const handleRemoveFilter = useCallback(
    (filter: LinkedTableFilter) => {
      setFilters((prev) => prev.filter((f) => f.id !== filter.id));
    },
    [setFilters]
  );

  // ============================================================================
  // Event Handlers - Row Groups
  // ============================================================================

  const handleAddToRowGroups = useCallback(
    (fieldId: string) => {
      setRowGroups((prev) => {
        if (prev.includes(fieldId)) return prev;
        return [...prev, fieldId];
      });
    },
    [setRowGroups]
  );

  const handleRemoveFromRowGroups = useCallback(
    (fieldId: string) => {
      setRowGroups((prev) => prev.filter((id) => id !== fieldId));
      // Also remove any sort for this field (can't ORDER BY a field not in GROUP BY)
      setRowGroupSorts((prev) => prev.filter((sort) => sort.field_id !== fieldId));
    },
    [setRowGroups, setRowGroupSorts]
  );

  const handleReorderRowGroups = useCallback(
    (newOrder: string[]) => {
      setRowGroups(newOrder);
    },
    [setRowGroups]
  );

  const handleUpdateRowGroupSort = useCallback(
    (fieldId: string, direction: 'asc' | 'desc' | 'none') => {
      setRowGroupSorts((prev) => {
        // Remove existing sort for this field
        const filtered = prev.filter((s) => s.field_id !== fieldId);
        // If 'none', just remove
        if (direction === 'none') {
          return filtered;
        }
        // Otherwise add the new sort
        return [...filtered, { field_id: fieldId, direction }];
      });
    },
    [setRowGroupSorts]
  );

  // ============================================================================
  // Event Handlers - Values
  // ============================================================================

  const handleAddToValues = useCallback(
    (fieldId: string, func: 'sum' | 'avg' | 'count' | 'min' | 'max') => {
      setValues((prev) => {
        // Check if this field is already in values
        if (prev.some((v) => v.field_id === fieldId)) return prev;
        return [...prev, { field_id: fieldId, function: func }];
      });
    },
    [setValues]
  );

  const handleUpdateValueFunction = useCallback(
    (fieldId: string, func: 'sum' | 'avg' | 'count' | 'min' | 'max') => {
      setValues((prev) =>
        prev.map((v) => (v.field_id === fieldId ? { ...v, function: func } : v))
      );
    },
    [setValues]
  );

  const handleRemoveFromValues = useCallback(
    (fieldId: string) => {
      setValues((prev) => prev.filter((v) => v.field_id !== fieldId));
    },
    [setValues]
  );

  const handleReorderValues = useCallback(
    (newValues: LinkedTableValue[]) => {
      setValues(newValues);
    },
    [setValues]
  );

  // ============================================================================
  // Event Handlers - Calculated Columns
  // ============================================================================

  const handleAddCalculatedColumn = useCallback(
    (column: LinkedTableCalculatedColumn) => {
      setCalculatedColumns((prev) => [...prev, column]);
    },
    [setCalculatedColumns]
  );

  const handleUpdateCalculatedColumn = useCallback(
    (updatedColumn: LinkedTableCalculatedColumn) => {
      setCalculatedColumns((prev) =>
        prev.map((c) => (c.id === updatedColumn.id ? updatedColumn : c))
      );
    },
    [setCalculatedColumns]
  );

  const handleRemoveCalculatedColumn = useCallback(
    (column: LinkedTableCalculatedColumn) => {
      setCalculatedColumns((prev) => prev.filter((c) => c.id !== column.id));
      // Also clean up any row_groups, values, and row_group_sorts that reference this calculated column
      setRowGroups((prev) => prev.filter((id) => id !== column.id));
      setValues((prev) => prev.filter((v) => v.field_id !== column.id));
      setRowGroupSorts((prev) => prev.filter((sort) => sort.field_id !== column.id));
    },
    [setCalculatedColumns, setRowGroups, setValues, setRowGroupSorts]
  );

  // ============================================================================
  // Event Handlers - Relationships
  // ============================================================================

  const handleAddRelationship = useCallback(
    async (newRelationship: LinkedTableRelationship) => {
      setRelationships((prev) => [...prev, newRelationship]);

      // Initialize version selection for new relationship (default to 'current')
      if (newRelationship.alias) {
        setVersionSelections((prev) => ({
          ...prev,
          [newRelationship.alias]: 'current',
        }));
      }
    },
    [setRelationships, setVersionSelections]
  );

  const handleUpdateRelationship = useCallback(
    (updatedRelationship: LinkedTableRelationship) => {
      // Find the current relationship from ref (stable, doesn't trigger re-renders)
      const oldRelationship = configRef.current.relationships.find(
        (r) => r.id === updatedRelationship.id
      );

      // Determine if we need to update selections
      const shouldUpdateSelections =
        (oldRelationship &&
          oldRelationship.alias !== updatedRelationship.alias &&
          updatedRelationship.alias) ||
        (updatedRelationship.alias && !oldRelationship?.alias);

      // Update relationships
      setRelationships((prevRelationships) =>
        prevRelationships.map((r) =>
          r.id === updatedRelationship.id ? updatedRelationship : r
        )
      );

      // Update version selections if needed (React 18 will batch this with the above setState)
      if (shouldUpdateSelections) {
        setVersionSelections((prevSel) => {
          const updatedSel = { ...prevSel };
          // Remove old alias entry if it exists
          if (
            oldRelationship?.alias &&
            oldRelationship.alias !== updatedRelationship.alias
          ) {
            delete updatedSel[oldRelationship.alias];
          }
          // Add new alias entry
          if (updatedRelationship.alias) {
            updatedSel[updatedRelationship.alias] = 'current';
          }
          return updatedSel;
        });
      }

      // Note: PrelinkedTable query invalidation happens in useEffect watching completeness changes
    },
    [setRelationships, setVersionSelections]
  );

  const handleRemoveRelationship = useCallback(
    (relationshipId: string) => {
      setRelationships((prev) => {
        const relationship = prev.find((r) => r.id === relationshipId);
        if (relationship?.alias) {
          setVersionSelections((prevSel) => {
            const updated = { ...prevSel };
            delete updated[relationship.alias];
            return updated;
          });
        }

        // Use refs to access current values without circular dependencies
        const currentConfig = configRef.current;
        const currentTableFields = tableFieldsRef.current;

        // Check if any calculated columns depend on this relationship
        const dependentColumns = currentConfig.calculated_columns.filter(
          (col) => col.formula_value?.includes(relationship?.alias || '')
        );

        if (dependentColumns.length > 0) {
          toast.warning(
            `${dependentColumns.length} calculated column(s) reference this relationship and may break`
          );
        }

        // Check if any filters depend on this relationship
        const removedAlias = relationship?.alias;
        const relationshipFieldIds = currentTableFields
          .filter((f) => f.table_alias === removedAlias)
          .map((f) => f.id);

        // Helper: check if a field ID (bare or qualified) belongs to the removed relationship
        const belongsToRemovedRelationship = (fieldId: string): boolean => {
          const parsed = parseFieldId(fieldId);
          if (parsed.alias) {
            // Qualified ID: check alias directly
            return parsed.alias === removedAlias;
          }
          // Bare ID: check against field list (backward compat)
          return relationshipFieldIds.includes(fieldId);
        };

        const dependentFilters = currentConfig.filters.filter((filter) =>
          belongsToRemovedRelationship(filter.field_id)
        );

        // Check if any row_groups, row_group_sorts, or values depend on this relationship
        const dependentRowGroups = currentConfig.row_groups.filter((fieldId) =>
          belongsToRemovedRelationship(fieldId)
        );
        const dependentRowGroupSorts = currentConfig.row_group_sorts.filter((sort) =>
          belongsToRemovedRelationship(sort.field_id)
        );
        const dependentValues = currentConfig.values.filter((val) =>
          belongsToRemovedRelationship(val.field_id)
        );

        // Remove dependent items
        if (dependentFilters.length > 0) {
          setFilters((prevFilters) =>
            prevFilters.filter(
              (f) => !belongsToRemovedRelationship(f.field_id)
            )
          );
        }

        if (dependentRowGroups.length > 0) {
          setRowGroups((prevRowGroups) =>
            prevRowGroups.filter((fieldId) => !belongsToRemovedRelationship(fieldId))
          );
        }

        if (dependentRowGroupSorts.length > 0) {
          setRowGroupSorts((prevSorts) =>
            prevSorts.filter((sort) => !belongsToRemovedRelationship(sort.field_id))
          );
        }

        if (dependentValues.length > 0) {
          setValues((prevValues) =>
            prevValues.filter((val) => !belongsToRemovedRelationship(val.field_id))
          );
        }

        // Show consolidated cleanup notification
        const cleanupDetails: string[] = [];
        if (dependentFilters.length > 0) {
          cleanupDetails.push(`${dependentFilters.length} filter(s)`);
        }
        if (dependentRowGroups.length > 0) {
          cleanupDetails.push(`${dependentRowGroups.length} row group(s)`);
        }
        if (dependentValues.length > 0) {
          cleanupDetails.push(`${dependentValues.length} value(s)`);
        }

        // Only show toast if there were dependent items cleaned up (informative)
        // Don't show toast for simple removal - the UI already reflects the change
        if (cleanupDetails.length > 0) {
          toast.linkedTable.relationshipRemovedWithDetails(cleanupDetails);
        }

        return prev.filter((r) => r.id !== relationshipId);
      });
    },
    [setRelationships, setVersionSelections, setFilters, setRowGroups, setRowGroupSorts, setValues]
  );

  // ============================================================================
  // Event Handlers - Variables
  // ============================================================================

  const handleAddVariable = useCallback(() => {
    const newVariable: LinkedTableVariable = {
      id: crypto.randomUUID(),
      name: '',
      type: 'text',
      default_value: '',
    };
    setVariables((prev) => [...prev, newVariable]);
  }, [setVariables]);

  const handleUpdateVariable = useCallback(
    (updatedVariable: LinkedTableVariable) => {
      setVariables((prev) =>
        prev.map((v) => (v.id === updatedVariable.id ? updatedVariable : v))
      );
    },
    [setVariables]
  );

  const handleRemoveVariable = useCallback(
    (variableId: string) => {
      // Clear variable_name on any filters referencing this variable.
      // If the filter has no static value, remove it entirely since it would be incomplete.
      const variable = configRef.current.variables.find((v) => v.id === variableId);
      if (variable) {
        setFilters((prev) =>
          prev.reduce<LinkedTableFilter[]>((acc, f) => {
            if (f.variable_name === variable.name) {
              // If filter has a usable static value, keep it without the variable binding
              const hasValue = f.value !== '' && f.value !== null && f.value !== undefined
                && !(Array.isArray(f.value) && f.value.length === 0);
              if (hasValue) {
                acc.push({ ...f, variable_name: undefined });
              }
              // Otherwise: drop the filter — it would be incomplete without a value source
            } else {
              acc.push(f);
            }
            return acc;
          }, [])
        );
      }
      setVariables((prev) => prev.filter((v) => v.id !== variableId));
    },
    [setVariables, setFilters]
  );

  // ============================================================================
  // Event Handlers - Version Selections
  // ============================================================================

  const handleUpdateVersionSelection = useCallback(
    (alias: string, selection: string) => {
      setVersionSelections((prev) => ({
        ...prev,
        [alias]: selection,
      }));
    },
    [setVersionSelections]
  );

  // ============================================================================
  // Backend Config Normalization Handler
  // ============================================================================

  /**
   * Handle normalized config from backend
   *
   * When the backend returns a normalized configuration (after cleaning invalid
   * references or auto-extending aggregations), we adopt it to keep frontend
   * in sync with backend state.
   *
   * @param normalizedConfig - Cleaned configuration from backend
   * @param changes - Metadata about what was changed
   */
  const handleConfigNormalized = useCallback(
    (
      normalizedConfig: LinkedTableNormalizedConfig,
      changes: LinkedTableConfigChanges
    ) => {
      // Only adopt if changes were made
      const hasChanges = Object.values(changes).some((count) => count > 0);
      if (!hasChanges) return;

      // Map API calculated columns to frontend format (add required 'formula' field)
      const mappedCalculatedColumns: LinkedTableCalculatedColumn[] =
        normalizedConfig.calculated_columns.map((col) => ({
          ...col,
          formula: col.formula_value, // Frontend requires 'formula' as canonical field
        }));

      // Update state with normalized config
      setFilters(normalizedConfig.filters as LinkedTableFilter[]);
      setCalculatedColumns(mappedCalculatedColumns);
      setRowGroups(normalizedConfig.row_groups);
      setValues(normalizedConfig.values as LinkedTableValue[]);

      // Build toast notification message for cleaned items
      const cleanedItems: string[] = [];
      if (changes.filters_removed > 0) {
        cleanedItems.push(`${changes.filters_removed} filter(s) removed`);
      }
      if (changes.calculated_columns_removed > 0) {
        cleanedItems.push(
          `${changes.calculated_columns_removed} calculated column(s) removed`
        );
      }
      if (changes.row_groups_cleaned > 0) {
        cleanedItems.push(`${changes.row_groups_cleaned} row group(s) cleaned`);
      }
      if (changes.values_cleaned > 0) {
        cleanedItems.push(`${changes.values_cleaned} value(s) cleaned`);
      }

      if (cleanedItems.length > 0) {
        toast.info(`Configuration updated: ${cleanedItems.join(', ')}`);
      }
    },
    [setFilters, setCalculatedColumns, setRowGroups, setValues]
  );

  // ============================================================================
  // Return API
  // ============================================================================

  return {
    // PrelinkedTable configuration (for TanStack Query cache busting)
    prelinkedTableConfig,
    prelinkedTableKey,

    // Incomplete counts for UI badges
    incompleteCounts,

    // Event handlers
    handleAddFilter,
    handleUpdateFilter,
    handleRemoveFilter,
    handleAddToRowGroups,
    handleRemoveFromRowGroups,
    handleReorderRowGroups,
    handleUpdateRowGroupSort,
    handleAddToValues,
    handleUpdateValueFunction,
    handleRemoveFromValues,
    handleReorderValues,
    handleAddCalculatedColumn,
    handleUpdateCalculatedColumn,
    handleRemoveCalculatedColumn,
    handleAddRelationship,
    handleUpdateRelationship,
    handleRemoveRelationship,
    handleAddVariable,
    handleUpdateVariable,
    handleRemoveVariable,
    handleUpdateVersionSelection,
    handleConfigNormalized,

    // Expose completeness checkers for external use
    isFilterComplete,
    isCalculatedColumnComplete,
    isRelationshipComplete,
  };
};
